import type { ImageFocus, ImageTone, TravelContentEntry } from "./types.js";

export interface AttractionVisual {
  image: string;
  alt: string;
  label: string;
  accentClass: string;
  objectPositionClass: string;
}

export const fallbackImage = "/images/stockholm-water.svg";

const toneClasses: Record<ImageTone, string> = {
  teal: "from-teal-950/85 via-teal-900/25 to-transparent",
  slate: "from-slate-950/85 via-slate-900/25 to-transparent",
  amber: "from-amber-950/80 via-amber-900/20 to-transparent",
  emerald: "from-emerald-950/80 via-emerald-900/20 to-transparent",
  violet: "from-violet-950/80 via-violet-900/20 to-transparent",
  rose: "from-rose-950/80 via-rose-900/20 to-transparent",
  stone: "from-stone-950/80 via-stone-900/20 to-transparent",
  cyan: "from-cyan-950/80 via-cyan-900/20 to-transparent",
};

const focusClasses: Record<ImageFocus, string> = {
  center: "object-center",
  top: "object-top",
  bottom: "object-bottom",
  left: "object-left",
  right: "object-right",
};

const supportedImageExtension = /\.(?:svg|png|jpe?g|webp|avif)$/i;

function isSupportedLocalImage(path: unknown): path is string {
  return typeof path === "string"
    && path.startsWith("/images/")
    && !path.includes("..")
    && supportedImageExtension.test(path);
}

function isImageTone(value: unknown): value is ImageTone {
  return typeof value === "string" && value in toneClasses;
}

function isImageFocus(value: unknown): value is ImageFocus {
  return typeof value === "string" && value in focusClasses;
}

export function getAttractionVisual(attraction: TravelContentEntry): AttractionVisual {
  const configuredImage = attraction.image;
  const image = isSupportedLocalImage(configuredImage?.src)
    ? configuredImage.src
    : fallbackImage;
  const tone = isImageTone(configuredImage?.tone) ? configuredImage.tone : "teal";
  const focus = isImageFocus(configuredImage?.focus) ? configuredImage.focus : "center";

  return {
    image,
    alt: typeof configuredImage?.alt === "string" ? configuredImage.alt : "",
    label: typeof configuredImage?.label === "string" ? configuredImage.label : "Stockholm-Erlebnis",
    accentClass: toneClasses[tone],
    objectPositionClass: focusClasses[focus],
  };
}
